import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DownloadService } from './download.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public apiUrl = environment.url;

  constructor(
    private http: HttpClient,
    private readonly downloadService: DownloadService,
  ) {}

  public getWithoutAuth<T>(endpoint: string, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    return this.http.get<T>(url, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public getWithResponseHeader<T>(endpoint: string, params?: any): Observable<any> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    return this.http.get<T>(url, { params: params, headers: headers, observe: 'response', responseType: 'text' as 'json' }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public get<T>(endpoint: string, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    headers.set('Accept', 'application/json');
    return this.http.get<T>(url, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public getIcon<T>(endpoint: string, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return this.http.get<T>(url, { params, headers, responseType: 'blob' as 'json' }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }
  public getDiagram<T>(endpoint: string): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    headers.set('content-type', 'image/png ');
    return this.http.get<T>(url, { headers, responseType: 'blob' as 'json' }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public getResourceLicenses<T>(endpoint: string): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Accept', 'text/html');
    headers.append('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return this.http.get(url, { headers: headers, responseType: 'text' }).pipe(catchError((error) => this.handleError(error)));
  }

  public getExport<T>(endpoint: string, filename: string, params?: any): Observable<void> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return new Observable<void>((observer) => {
      this.http.get<T>(url, { params, headers, responseType: 'blob' as 'json', observe: 'response' }).subscribe({
        next: (response: any) => {
          this.downloadService.downloadFile(response, filename);
          observer.next();
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          this.handleError(error);
        },
      });
    });
  }

  public post<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return this.http.post<T>(url, body, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public postWithResponseHeader<T>(endpoint: string, body: any, params?: any): Observable<any> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return this.http.post<T>(url, body, { params: params, headers: headers, observe: 'response' });
  }

  public postWithoutAuth<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    return this.http.post<T>(url, body, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public postIcon<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    headers.set('Accept', 'application/json');
    headers.set('enctype', 'multipart/form-data');
    return this.http.post<T>(url, body, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public postFile<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return this.http.post<T>(url, body, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public put<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return this.http.put<T>(url, body, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public patch<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return this.http.patch<T>(url, body, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public delete<T>(endpoint: string, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    return this.http.delete<T>(url, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public deleteIcon<T>(endpoint: string, params?: any): Observable<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
    headers.set('Accept', '*/*');
    return this.http.delete<T>(url, { params: params, headers: headers }).pipe(
      map((data) => data),
      catchError(this.handleError),
    );
  }

  public postExport<T>(endpoint: string, body: any, filename: string, params?: any): Observable<void> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);

    return new Observable<void>((observer) => {
      this.http.post<T>(url, body, { params, headers, responseType: 'blob' as 'json', observe: 'response' }).subscribe({
        next: (response: any) => {
          this.downloadService.downloadFile(response, filename);
          observer.next();
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          this.handleError(error);
        },
      });
    });
  }

  private handleError(error: any): Observable<any> {
    return throwError(error);
  }
}
